import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { HeroCalculator } from '../components/landing/HeroCalculator';
import { ExampleCalculation } from '../components/landing/ExampleCalculation';
import Footer from '../components/Footer';
import { LicenseConfiguratorModal } from '../components/LicenseConfiguratorModal';
import { ReferralBanner } from '../components/ReferralBanner';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { 
  Shield, Users, Clock, CheckCircle2, Star, ChevronDown, ChevronUp, 
  Send, MessageSquare, Zap, FileCheck, Building2, Award, ArrowRight,
  Bot, Loader2, X, Plus, Minus, Quote, HardHat, CreditCard,
  BookOpen, AlertTriangle, TrendingUp, UserCheck, Play
} from 'lucide-react';

// ============================================================================
// PETRA KI-CHATBOT
// ============================================================================
const PetraChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `petra_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hallo! Ich bin **Petra**, Ihre digitale Beraterin bei der SmartVolt Academy.\n\nIch helfe Ihnen, die richtige Schulungslösung für Ihren Betrieb zu finden.\n\n**In welchem Gewerk sind Sie tätig?**`
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petra/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMessage, history: messages })
      });
      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Entschuldigung, da ist etwas schiefgelaufen. Bitte versuchen Sie es erneut.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setIsOpen(!isOpen)} className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-yellow-600 rounded-full shadow-lg shadow-yellow-600/30 flex items-center justify-center hover:bg-yellow-500 transition-all" data-testid="petra-chat-btn" aria-label="Chat öffnen">
        {isOpen ? <X className="w-6 h-6 text-black" /> : <MessageSquare className="w-6 h-6 text-black" />}
      </button>
      {isOpen && (
        <div className="fixed bottom-24 left-6 z-50 w-[340px] sm:w-96 bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '70vh' }} data-testid="petra-chat-window">
          <div className="p-4 border-b border-gray-800 flex items-center gap-3">
            <div className="w-9 h-9 bg-yellow-500 rounded-full flex items-center justify-center"><Bot className="w-5 h-5 text-black" /></div>
            <div><p className="font-bold text-white text-sm">Petra</p><p className="text-xs text-green-400">Online</p></div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '45vh' }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-yellow-600 text-black rounded-br-sm' : 'bg-gray-800 text-gray-200 rounded-bl-sm'}`}>
                  {msg.content.split('\n').map((line, i) => <p key={i} className={i > 0 ? 'mt-1' : ''}>{line.replace(/\*\*(.*?)\*\*/g, '$1')}</p>)}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-gray-800 p-3 rounded-2xl rounded-bl-sm"><Loader2 className="w-5 h-5 text-yellow-500 animate-spin" /></div></div>}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 border-t border-gray-800">
            <div className="flex gap-2">
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && sendMessage()} placeholder="Nachricht..." className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-4 py-2 text-white text-sm focus:outline-none focus:border-yellow-500" disabled={loading} />
              <button onClick={sendMessage} disabled={loading || !input.trim()} className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center disabled:opacity-50 hover:bg-yellow-500 transition"><Send className="w-4 h-4 text-black" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// STERNE-BEWERTUNG
// ============================================================================
const StarRating = ({ rating = 4.89, size = "lg" }) => {
  const fullStars = Math.floor(rating);
  const starSize = size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`${starSize} ${i < fullStars ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
      ))}
      <span className={`ml-2 font-bold ${size === "lg" ? "text-lg" : "text-sm"} text-yellow-500`}>{rating}</span>
    </div>
  );
};

// ============================================================================
// PREISRECHNER (Inline-Variante in der Pricing-Sektion)
// Tarif wählen → direkt zum Digistore24-Checkout (14-Tage-Testphase)
// ============================================================================

// ============================================================================
// LANDING PAGE
// ============================================================================
const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configMode, setConfigMode] = useState('buy'); // 'buy' | 'trial'
  const location = useLocation();

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (location.hash === '#pricing') setTimeout(scrollToPricing, 300);
  }, [location.hash]);

  const openConfigurator = (mode) => {
    setConfigMode(mode);
    setConfigOpen(true);
  };

  const handleCheckout = async () => {
    // Lizenz-Konfigurator zeigt der Nutzer zuerst die Mengenwahl
    openConfigurator('buy');
  };

  const faqs = [
    { q: "Wie funktioniert die Compliance-Flatrate?", a: "Sie zahlen 10,90€ pro Mitarbeiter/Monat. Dafür erhalten alle Mitarbeiter Zugang zu sämtlichen Pflichtschulungen. Zertifikate werden automatisch erstellt." },
    { q: "Werden die Zertifikate von der BG anerkannt?", a: "Ja, unsere Zertifikate entsprechen den Anforderungen nach DGUV Vorschrift 1 und werden von Berufsgenossenschaften und Behörden anerkannt." },
    { q: "Kann ich jederzeit kündigen?", a: "Ja, monatlich kündbar. Ihr Zugang bleibt bis zum Ende des bezahlten Monats aktiv. Kein Risiko." },
    { q: "Warum muss ich für den Test Zahlungsdaten angeben?", a: "Damit Ihr Betrieb nach der Testphase nahtlos weiterlernen kann. Heute zahlen Sie 0,00 € – die erste Abbuchung über Digistore24 erfolgt erst nach 14 Tagen. Kündigen Sie vorher, zahlen Sie nichts." },
    { q: "Was passiert bei einer BG-Prüfung?", a: "Alle Nachweise sind im Chef-Cockpit auf Knopfdruck abrufbar. Sie exportieren die Zertifikate als PDF — fertig." },
    { q: "Was kostet das für einen 10-Mann-Betrieb?", a: "10 Lizenzen × 10,90 € = 109 €/Monat. Weniger als ein Werkzeugschaden — aber voller Schutz vor Bußgeldern." },
  ];

  const benefits = [
    { icon: Shield, title: 'Rechtssicherheit', desc: 'Alle DGUV-, VDE- und BetrSichV-Schulungen abgedeckt' },
    { icon: Clock, title: '15 Min. pro Schulung', desc: 'Kein Baustellenstopp — Schulung im eigenen Tempo' },
    { icon: FileCheck, title: 'Auto-Zertifikate', desc: 'BG-konforme Nachweise per Knopfdruck' },
    { icon: Users, title: 'Chef-Cockpit', desc: 'Alle Mitarbeiter-Stände auf einen Blick' },
  ];

  const problems = [
    { icon: AlertTriangle, text: "Bußgelder bis 25.000€ bei fehlenden Unterweisungen" },
    { icon: Clock, text: "Stundenlanger Papierkram für Schulungsnachweise" },
    { icon: Users, text: "Mitarbeiter von der Baustelle abziehen für Schulungen" },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Helmet>
        <title>SmartVolt Academy - Rechtssichere Mitarbeiter-Unterweisungen für Handwerksbetriebe</title>
        <meta name="description" content="Rechtssichere DGUV-Pflichtschulungen in 15 Minuten. Vom Elektromeister entwickelt. Ab 10,90€/Mitarbeiter/Monat. Automatische Zertifikate, Chef-Cockpit." />
        <meta name="keywords" content="DGUV Unterweisung, Elektrofachkraft Schulung, Pflichtschulung Handwerk, Sicherheitsunterweisung, VDE, BG Nachweis, Elektromeister" />
      </Helmet>

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-gray-950/95 backdrop-blur-md border-b border-gray-800/50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img
              src="/assets/smartvolt-logo.png"
              alt="SmartVolt Academy"
              className="h-14 sm:h-16 lg:h-[72px] w-auto drop-shadow-lg"
              loading="eager"
            />
          </Link>
          <div className="flex items-center gap-1 sm:gap-6">
            <Link to="/kurse" className="text-gray-300 hover:text-yellow-400 transition text-sm font-medium hidden md:block px-2 py-1" data-testid="nav-kurse">
              Kurse
            </Link>
            <a href="#pricing" className="text-gray-300 hover:text-yellow-400 transition text-sm font-medium hidden md:block px-2 py-1" data-testid="nav-preise">
              Preise
            </a>
            <a href="#meister" className="text-gray-300 hover:text-yellow-400 transition text-sm font-medium hidden lg:block px-2 py-1" data-testid="nav-meister">
              Für Chefs
            </a>
            <Link to="/login" className="text-gray-300 hover:text-yellow-400 transition text-sm font-medium px-2 py-1" data-testid="nav-login">
              Login
            </Link>
            <Button
              onClick={scrollToPricing}
              size="sm"
              className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400 font-bold text-sm shadow-md shadow-yellow-500/20"
              data-testid="nav-cta-btn"
            >
              14 Tage testen
            </Button>
          </div>
        </div>
      </nav>

      <ReferralBanner />

      {/* HERO — Conversion-optimiert · 2-Spalten */}
      <section className="pt-24 sm:pt-28 pb-12 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/5 via-transparent to-transparent" />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] bg-amber-500/8 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

            {/* LINKS: Content */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Trust-Badge oben */}
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5 mb-5" data-testid="hero-trust-badge">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-green-400 text-sm font-medium">DGUV-konform · BG-anerkannt</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-5" data-testid="hero-headline">
                Rechtssichere<br/>
                <span className="text-yellow-500">Mitarbeiter-Unterweisungen</span>
                <br/>in 15 Minuten.
              </h1>

              <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto lg:mx-0 mb-6 leading-relaxed">
                <strong className="text-yellow-400">0 % Papierkram für den Chef.</strong> Nach BetrSichV &amp; DGUV V1.
                Vom Elektromeister für alle Handwerksbetriebe.
              </p>

              {/* DGUV/BG-Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-6" data-testid="hero-badges-row">
                <span className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/40 text-green-300 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide">
                  <Shield className="w-3.5 h-3.5" /> BetrSichV
                </span>
                <span className="flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide">
                  <Award className="w-3.5 h-3.5" /> DGUV V1 &amp; V3
                </span>
                <span className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/40 text-blue-300 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide">
                  <FileCheck className="w-3.5 h-3.5" /> BG-Nachweis-Export
                </span>
              </div>

              {/* Vorteilspunkte */}
              <ul className="space-y-2.5 mb-8 text-left max-w-xl mx-auto lg:mx-0" data-testid="hero-benefits-list">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-200 text-sm sm:text-base"><strong>Automatische Zertifikate</strong> — rechtssicher, personalisiert, sofort per PDF</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-200 text-sm sm:text-base"><strong>Chef-Cockpit</strong> — Fortschritt aller Mitarbeiter in Echtzeit</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-200 text-sm sm:text-base"><strong>ZIP-Export für BG-Prüfer</strong> — ein Klick, alle Nachweise</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-200 text-sm sm:text-base"><strong>15 Min. pro Kurs</strong> — mobil auf Smartphone &amp; Tablet</span>
                </li>
              </ul>

              {/* Haupt-CTA mit Mitarbeiter-Rechner → Digistore24-Checkout (?quantity=X) */}
              <HeroCalculator />

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500 mt-4">
                <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-blue-500" /> Sofortzugang</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500" /> Heute 0,00 €</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500" /> Jederzeit kündbar</span>
              </div>

              {/* Digistore24-Pflichthinweis: klare Lieferart */}
              <div
                className="mt-3 flex items-start gap-2 rounded-lg bg-blue-500/10 border border-blue-500/30 px-3 py-2 max-w-xl"
                data-testid="hero-delivery-notice"
              >
                <Zap className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-blue-100 text-xs leading-relaxed">
                  <strong>100 % Digitaler Sofort-Zugang</strong> — Nach dem Kauf erhalten Sie direkt Ihre Zugangsdaten per E-Mail für das Online-Portal.
                </p>
              </div>

              {/* Enthaltene Inhalte (Digistore24-Transparenz) */}
              <div
                className="mt-2 rounded-lg bg-black/40 border border-gray-800 px-3 py-2 max-w-xl"
                data-testid="hero-content-scope"
              >
                <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1">Enthaltene Inhalte</p>
                <p className="text-gray-300 text-xs leading-relaxed">
                  Vollzugriff auf rechtssichere Arbeits- &amp; Arbeitsschutz-Unterweisungen: <strong className="text-white">BetrSichV</strong>, <strong className="text-white">DGUV Vorschrift 1</strong>, <strong className="text-white">VDE 0105-100</strong>, <strong className="text-white">Leitern &amp; Gerüste</strong>, <strong className="text-white">Baustellen-Recht</strong> · interaktive Wissens-Checks · automatische PDF-Zertifikate.
                </p>
              </div>

              <div className="flex items-center gap-3 mt-6 text-sm text-gray-400 justify-center lg:justify-start" data-testid="hero-social-proof">
                <StarRating rating={4.89} size="sm" />
                <span>· 500+ Handwerksbetriebe</span>
              </div>

              {/* Kompakte Meister-Signatur — OBERHALB DER FOLD sichtbar */}
              <a
                href="mailto:smartvolt-academy@gmx.net?subject=Frage%20zu%20SmartVolt%20Academy"
                className="mt-5 group inline-flex items-center gap-3 rounded-2xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/[0.08] to-amber-500/[0.05] hover:border-yellow-500/70 hover:from-yellow-500/[0.12] hover:to-amber-500/[0.08] transition-all px-4 py-3 shadow-lg shadow-yellow-500/5 hover:shadow-yellow-500/15 max-w-md"
                data-testid="hero-meister-signature"
              >
                <div className="relative shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-yellow-500/60 shadow-md shadow-yellow-500/20">
                    <img
                      src="/assets/martin-seidel.webp"
                      alt="Martin Seidel"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 25%' }}
                      loading="eager"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full w-4 h-4 border-2 border-gray-950 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  </div>
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-white text-sm font-bold leading-tight">
                    <span className="text-yellow-400">Hi, ich bin Martin</span> — schreib mir persönlich
                  </p>
                  <p className="text-gray-300 text-xs mt-0.5 leading-snug">
                    Elektromeister &amp; Gründer · Antwort meist innerhalb 24 h
                  </p>
                  <p className="text-yellow-300 text-[11px] font-mono mt-1 group-hover:text-yellow-200 truncate">
                    ✉️ smartvolt-academy@gmx.net
                  </p>
                </div>
              </a>
            </div>

            {/* RECHTS: Persönliches Meister-Portrait — der Mensch dahinter */}
            <div className="lg:col-span-5 relative" data-testid="hero-meister-portrait">
              <div className="relative mx-auto max-w-md">

                {/* Floating „Bestanden!"-Bubble oben rechts */}
                <div className="absolute -top-3 -right-3 sm:-right-6 z-20 bg-green-500 text-white rounded-full px-3 py-1.5 shadow-lg shadow-green-500/40 flex items-center gap-1.5 rotate-[6deg]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs font-black">Bestanden!</span>
                </div>

                {/* Großes Portrait */}
                <div
                  className="relative rounded-[28px] overflow-hidden border-4 border-yellow-500/50 shadow-2xl shadow-yellow-500/20"
                  style={{ aspectRatio: '4 / 5', background: '#0f0f14' }}
                >
                  <img
                    src="/assets/martin-seidel.webp"
                    alt="Martin Seidel — Elektromeister & Gründer von SmartVolt Academy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 20%' }}
                    loading="eager"
                    fetchPriority="high"
                    data-testid="hero-meister-photo"
                  />
                  {/* Farbverlauf unten für Text-Lesbarkeit */}
                  <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                  {/* Verified-Badge oben rechts */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-green-500/50 rounded-full px-3 py-1.5">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-green-300 text-[11px] font-bold uppercase tracking-wide">Verifiziert</span>
                  </div>

                  {/* Meister-Siegel oben links */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-yellow-500/95 rounded-full px-3 py-1.5 shadow-lg">
                    <Award className="w-4 h-4 text-black" />
                    <span className="text-black text-[11px] font-black uppercase tracking-wide">Elektromeister</span>
                  </div>

                  {/* Name + Titel unten */}
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <p className="text-white text-2xl font-black leading-tight">Martin Seidel</p>
                    <p className="text-yellow-300 text-sm font-semibold mt-0.5">Elektromeister · Gründer SmartVolt Academy</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="flex items-center gap-1 text-[10px] text-gray-200 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-0.5">
                        <HardHat className="w-3 h-3 text-yellow-400" /> 20+ Jahre Praxis
                      </span>
                      <span className="flex items-center gap-1 text-[10px] text-gray-200 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2 py-0.5">
                        <Shield className="w-3 h-3 text-green-400" /> HWK-Vollversammlung
                      </span>
                    </div>
                  </div>
                </div>

                {/* Schwebendes Muster-Zertifikat unten links */}
                <div
                  className="absolute -bottom-4 -left-6 sm:-left-14 w-48 sm:w-56 rotate-[-8deg] rounded-lg overflow-hidden shadow-2xl border-2 border-yellow-500 hidden sm:block"
                  style={{ background: 'linear-gradient(135deg, #0f0f14 0%, #1a1a24 100%)' }}
                  data-testid="hero-certificate-preview"
                >
                  <div className="p-3 text-center relative">
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-yellow-500/10 text-4xl font-black rotate-[-15deg]">MUSTER</span>
                    </div>
                    <p className="text-yellow-400 text-[8px] tracking-[0.25em] font-bold uppercase relative">SmartVolt Academy</p>
                    <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto my-1.5 relative">
                      <Award className="w-5 h-5 text-yellow-400" />
                    </div>
                    <p className="text-white font-bold text-[11px] relative">Teilnahmebescheinigung</p>
                    <div className="my-1.5 h-px bg-yellow-500/50" />
                    <p className="text-yellow-300 font-bold text-xs relative">Max Mustermann</p>
                    <p className="text-gray-400 text-[8px] mt-0.5 relative">DGUV V1 · BetrSichV</p>
                    <p className="text-gray-500 text-[7px] mt-1 font-mono relative">SVA-2026-047821</p>
                  </div>
                </div>

                {/* Kleines Tablet-Cockpit unten rechts (Produkt-Beweis) */}
                <div
                  className="absolute -bottom-6 -right-4 sm:-right-10 w-40 sm:w-48 rotate-[6deg] rounded-xl overflow-hidden shadow-2xl border-2 border-gray-700 bg-gray-950 hidden sm:block"
                  data-testid="hero-cockpit-preview"
                >
                  <div className="p-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-[8px] text-gray-500 uppercase tracking-wider font-bold">Chef-Cockpit</p>
                      <div className="w-4 h-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <HardHat className="w-2.5 h-2.5 text-yellow-400" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <div className="rounded bg-green-500/10 border border-green-500/30 p-1">
                        <p className="text-[7px] text-green-400 font-bold uppercase">Ok</p>
                        <p className="text-white font-black text-xs">14/15</p>
                      </div>
                      <div className="rounded bg-yellow-500/10 border border-yellow-500/30 p-1">
                        <p className="text-[7px] text-yellow-400 font-bold uppercase">Zerts</p>
                        <p className="text-white font-black text-xs">23</p>
                      </div>
                    </div>
                    <div className="rounded bg-gray-900 border border-gray-800 p-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-white text-[7px]">Max M.</span>
                        <span className="text-[7px] text-gray-400">100%</span>
                      </div>
                      <div className="h-0.5 rounded-full bg-gray-800 overflow-hidden">
                        <div className="h-full bg-green-500 w-full" />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MEISTER-GARANTIE-BOX — persönlicher Trust-Boost direkt unter Hero */}
      <section id="meister" className="px-4 pb-14 pt-2" data-testid="meister-garantie-box">
        <div className="container mx-auto max-w-4xl">
          <div className="relative rounded-2xl border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/[0.08] via-gray-900/60 to-amber-500/[0.06] p-6 sm:p-8 shadow-2xl shadow-yellow-500/10 backdrop-blur-sm">
            {/* Deko-Glow */}
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-yellow-500/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-center">
              {/* Foto-Platzhalter (später gegen echtes Foto tauschen) */}
              <div className="mx-auto sm:mx-0">
                <div className="relative">
                  <div
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-yellow-500/60 shadow-xl shadow-yellow-500/20"
                    data-testid="meister-photo-placeholder"
                  >
                    <img
                      src="/assets/martin-seidel.webp"
                      alt="Martin Seidel – Elektromeister & Gründer"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 25%' }}
                      loading="eager"
                    />
                  </div>
                  {/* Meister-Siegel */}
                  <div className="absolute -bottom-2 -right-2 bg-yellow-500 text-black rounded-full w-9 h-9 flex items-center justify-center shadow-lg font-black text-xs border-2 border-gray-950">
                    ✓
                  </div>
                </div>
              </div>

              {/* Zitat + Signatur */}
              <div>
                <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold uppercase tracking-widest mb-2">
                  <Award className="w-4 h-4" />
                  <span>Entwickelt von Meisterhand</span>
                </div>
                <h3 className="text-white text-xl sm:text-2xl font-black leading-tight mb-3">
                  „Für den <span className="text-yellow-400">Handwerksalltag</span> — nicht für die Bürokratie."
                </h3>
                <div className="relative">
                  <Quote className="absolute -left-1 -top-1 w-6 h-6 text-yellow-500/30" />
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed pl-6 italic">
                    Ich bin selbst seit 15 Jahren <strong className="text-yellow-400 not-italic">Elektromeister &amp; Bauleiter</strong>
                    {' '}und habe SmartVolt Academy nach dem gebaut, was mir und meinem Team im Alltag wirklich fehlt:
                    rechtssichere Unterweisungen ohne stundenlanges Basteln — direkt auf dem Smartphone der Monteure.
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3 pl-6">
                  <div className="flex items-center gap-2">
                    <div className="h-px w-8 bg-yellow-500/60" />
                    <span className="text-yellow-300 font-bold text-sm">Martin Seidel</span>
                    <span className="text-gray-500 text-xs">· Gründer &amp; Elektromeister</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRIAL-CTA (goldene Box) → Tarifwahl → Digistore24 */}
      <section className="px-4 pb-14">
        <div className="container mx-auto max-w-3xl">
          <div
            className="rounded-2xl border-2 border-yellow-500/60 bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-yellow-500/15 p-6 shadow-2xl shadow-yellow-500/20"
            data-testid="flagship-free-cta"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-widest text-yellow-300 font-bold">14-Tage-Testzugang für Betriebe</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-white text-center mb-3" data-testid="trial-box-headline">
              Starten Sie jetzt Ihren <span className="text-yellow-400">14-Tage-Testzugang</span> für Ihren Betrieb.
            </h3>
            <ul className="text-gray-200 text-sm sm:text-base mb-4 space-y-1.5 max-w-xl mx-auto" data-testid="trial-box-text">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" /> Vollzugriff auf alle DGUV- &amp; VDE-Unterweisungen.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" /> Mitarbeiter sofort per Link oder QR-Code einladen.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                <span><strong className="text-yellow-300">Heute 0,00 €</strong> – Zahlungsdaten werden nur hinterlegt, Abbuchung erst nach der Testphase, jederzeit kündbar.</span>
              </li>
            </ul>
            <div className="flex justify-center">
              <Button
                onClick={scrollToPricing}
                size="lg"
                className="bg-yellow-500 text-black font-bold hover:bg-yellow-400 px-6 shadow-lg"
                data-testid="flagship-cta-btn"
              >
                <Play className="w-4 h-4 mr-2" /> Testzugang starten — heute 0,00 €
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM-SECTION */}
      <section className="py-12 px-4 bg-red-500/3 border-y border-red-500/10">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-lg sm:text-xl font-bold text-center text-white mb-6">Kennen Sie das?</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {problems.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                <p.icon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">{p.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-yellow-500 font-semibold mt-6 text-sm">Die SmartVolt Academy löst alle drei Probleme — automatisch.</p>
        </div>
      </section>

      {/* VORTEILE */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-3">Warum Handwerksbetriebe auf SmartVolt setzen</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">Alles was Sie für rechtssichere Unterweisungen brauchen</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, idx) => (
              <Card key={idx} className="bg-gray-900/50 border-gray-800 hover:border-yellow-600/40 transition-all text-center">
                <CardContent className="p-5">
                  <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <b.icon className="w-6 h-6 text-yellow-500" />
                  </div>
                  <h3 className="font-bold text-white mb-1 text-sm">{b.title}</h3>
                  <p className="text-xs text-gray-400">{b.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ÜBER MICH — Vertrauen */}
      <section className="py-16 px-4 bg-gray-900/30" data-testid="ueber-mich-section">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-gray-900/80 border border-yellow-500/20 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <HardHat className="w-10 h-10 text-yellow-500" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 text-center sm:text-left">
                  Vom Elektromeister — für Meisterbetriebe
                </h2>
                <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
                  Ich bin selbst <strong className="text-yellow-500">Elektromeister</strong> und <strong className="text-yellow-500">Bauleiter</strong> — 
                  und kenne den Spagat zwischen Termindruck und Vorschriften aus dem Alltag. 
                  Als <strong className="text-yellow-500">Mitglied der Vollversammlung der Handwerkskammer</strong> weiß ich, 
                  was Betriebe wirklich brauchen: Schulungen, die rechtssicher sind und nicht den halben Tag kosten.
                </p>
                <p className="text-gray-400 mt-3 text-sm sm:text-base">
                  Deshalb habe ich die SmartVolt Academy gebaut — <strong className="text-white">aus der Praxis, für die Praxis.</strong>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {['Elektromeister', 'Bauleiter', 'HWK-Vollversammlung', '20+ Jahre Praxis'].map((tag) => (
                <span key={tag} className="bg-yellow-500/10 text-yellow-500 text-xs px-3 py-1.5 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KURS-ÜBERSICHT */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-xl sm:text-2xl font-bold mb-3">16 Schulungen — eine Flatrate</h2>
          <p className="text-gray-500 text-sm mb-8">Alle Pflichtunterweisungen für Elektrobetriebe</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { icon: <Zap className="w-6 h-6" />, title: "DGUV V1", sub: "Prävention" },
              { icon: <Shield className="w-6 h-6" />, title: "VDE 0105", sub: "Anlagenbetrieb" },
              { icon: <AlertTriangle className="w-6 h-6" />, title: "Leitern", sub: "Absturzsicherung" },
              { icon: <Building2 className="w-6 h-6" />, title: "Baustelle", sub: "Baustellensicherheit" },
              { icon: <Zap className="w-6 h-6" />, title: "PV Montage", sub: "DC/AC-Systeme" },
              { icon: <Shield className="w-6 h-6" />, title: "PSA Absturz", sub: "Höhensicherung" },
              { icon: <TrendingUp className="w-6 h-6" />, title: "Smart Home", sub: "KNX/Loxone/Shelly" },
              { icon: <BookOpen className="w-6 h-6" />, title: "KI im Handwerk", sub: "ChatGPT & mehr" },
            ].map((c, i) => (
              <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 text-center hover:border-yellow-500/40 transition cursor-pointer" onClick={() => navigate('/kurse')}>
                <div className="text-yellow-500 flex justify-center mb-2">{c.icon}</div>
                <h4 className="font-bold text-white text-sm">{c.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{c.sub}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate('/kurse')} variant="outline" className="mt-6 border-gray-700 text-gray-400 hover:text-white" data-testid="view-all-courses-btn">
            Alle 16 Kurse ansehen <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            onClick={scrollToPricing}
            className="mt-3 sm:mt-6 sm:ml-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold hover:from-yellow-400 hover:to-amber-400"
            data-testid="try-lesson-btn"
          >
            14-Tage-Testzugang starten
          </Button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gray-900/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Das sagen unsere Kunden</h2>
            <div className="flex justify-center"><StarRating rating={4.89} size="sm" /></div>
            <p className="text-gray-500 text-sm mt-1">500+ Handwerksbetriebe vertrauen SmartVolt</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { name: "Thomas Berger", role: "GF, Berger Elektrotechnik", emp: "12 MA", text: "Endlich den Kopf frei für Projekte statt Papierkram. Die automatischen Zertifikate sparen mir jede Woche Stunden." },
              { name: "Sandra Hoffmann", role: "Inhaberin, Hoffmann Gebäudetechnik", emp: "8 MA", text: "Bei der letzten BG-Prüfung hatte ich alle Nachweise auf Knopfdruck parat. Der Prüfer war beeindruckt." },
              { name: "Michael Weber", role: "Betriebsleiter, Weber & Söhne", emp: "23 MA", text: "10,90€ pro Mitarbeiter — günstiger als ein Werkzeugschaden. Dafür haben wir komplette Rechtssicherheit." },
            ].map((t, i) => (
              <Card key={i} className="bg-gray-900/80 border-gray-800 hover:border-yellow-600/40 transition">
                <CardContent className="p-5">
                  <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}</div>
                  <p className="text-gray-300 text-sm italic mb-4">&ldquo;{t.text}&rdquo;</p>
                  <div className="border-t border-gray-800 pt-3">
                    <p className="font-semibold text-white text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role} &middot; {t.emp}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PREISRECHNER */}
      <section id="preise" className="py-16 px-4 scroll-mt-24" data-testid="pricing-section">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-2">Transparent. Fair. Kündbar.</h2>
          <p className="text-gray-500 text-center mb-8 text-sm">Keine versteckten Kosten. Alle Schulungen inklusive.</p>
          <ExampleCalculation />
          <div className="flex justify-center mt-6">
            <Button onClick={scrollToPricing} size="lg" className="bg-yellow-500 text-black font-bold hover:bg-yellow-400" data-testid="example-calc-cta-btn">
              Mitarbeiterzahl wählen &amp; 14 Tage testen <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {/* Digistore24-Pflichtblock: enthaltene Inhalte + Lieferart im Kaufkontext */}
          <div
            className="mt-8 rounded-xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.08] to-transparent p-5"
            data-testid="pricing-scope-notice"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
              <div>
                <p className="text-yellow-400 text-[11px] font-black uppercase tracking-widest mb-2">
                  Enthalten in allen Paketen
                </p>
                <p className="text-gray-200 leading-relaxed">
                  Vollzugriff auf rechtssichere Arbeits- &amp; Arbeitsschutz-Unterweisungen:{' '}
                  <strong>BetrSichV</strong>, <strong>DGUV Vorschrift 1</strong>,{' '}
                  <strong>VDE 0105-100</strong>, <strong>Leitern &amp; Gerüste</strong>,{' '}
                  <strong>Baustellen-Recht</strong>, interaktive Wissens-Checks und automatische
                  PDF-Zertifikate.
                </p>
              </div>
              <div>
                <p className="text-blue-300 text-[11px] font-black uppercase tracking-widest mb-2">
                  Lieferart · Bildungsgut
                </p>
                <p className="text-gray-200 leading-relaxed">
                  <strong>100 % Digitaler Sofort-Zugang</strong> — Nach dem Kauf erhalten Sie
                  direkt Ihre Zugangsdaten per E-Mail für das Online-Portal. Die Abbuchung
                  erfolgt durch{' '}
                  <a
                    href="https://www.digistore24.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 hover:underline"
                  >
                    Digistore24.com
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-900/30">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8">Häufige Fragen</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-900/60 border border-gray-800 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full p-4 flex items-center justify-between text-left">
                  <span className="font-medium text-white text-sm">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp className="w-4 h-4 text-yellow-500 flex-shrink-0 ml-2" /> : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" />}
                </button>
                {openFaq === idx && <div className="px-4 pb-4 text-gray-400 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Schützen Sie Ihren Betrieb — <span className="text-yellow-500">ab heute.</span></h2>
          <p className="text-gray-400 mb-6 text-sm">14 Tage testen. Heute 0,00 €. Jederzeit kündbar.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={scrollToPricing} size="lg" className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold px-10 py-5" data-testid="cta-final-btn">
              Jetzt Betrieb absichern <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — Trust-Boost vor dem Footer */}
      <TestimonialsSection />

      {/* FOOTER */}
      <Footer />

      {/* PETRA CHAT */}
      <PetraChatWidget />

      {/* FEEDBACK-Widget (schwebend rechts unten) */}
      <FeedbackWidget />

      {/* Lizenz-Konfigurator (Mengen-Auswahl mit dynamischem Preis) */}
      <LicenseConfiguratorModal
        open={configOpen}
        onOpenChange={setConfigOpen}
        mode={configMode}
      />
    </div>
  );
};

export default LandingPage;
